import React, {createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

const GAP = 6;
const EDGE = 16;
const MAX_WIDTH = 380;

/**
 * Dónde se dibuja el popover: debajo del disparador, alineado a su borde izquierdo y metido en la ventana
 * con 16 px de margen (a 390 px ocupa el ancho menos los márgenes). Si abajo no entra y arriba sí, se abre
 * hacia arriba; si no entra en ninguno, se pega al borde y scrollea adentro. Exportado para testearlo.
 */
export function popoverPosition(anchor, height, viewport) {
    const width = popoverWidth(viewport);
    const left = Math.min(Math.max(anchor.left, EDGE), viewport.width - width - EDGE);
    const below = anchor.bottom + GAP;
    const above = anchor.top - GAP - height;
    if (below + height <= viewport.height - EDGE) return {top: below, left, width, maxHeight: null};
    if (above >= EDGE) return {top: above, left, width, maxHeight: null};
    const maxHeight = viewport.height - 2 * EDGE;
    return {top: Math.max(EDGE, Math.min(below, viewport.height - EDGE - Math.min(height, maxHeight))), left, width, maxHeight};
}

/** El ancho del panel: 380 px, o la ventana menos los márgenes (a 390 px, 358). */
export function popoverWidth(viewport) {
    return Math.min(MAX_WIDTH, viewport.width - 2 * EDGE);
}

function viewport() {
    return {width: window.innerWidth, height: window.innerHeight};
}

const TABBABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), '
    + 'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function tabbablesOf(panel) {
    return Array.from(panel.querySelectorAll(TABBABLE));
}

function samePosition(current, next) {
    return !!current && current.top === next.top && current.left === next.left && current.width === next.width
        && current.maxHeight === next.maxHeight;
}

// El alto del contenido entero, aunque el panel ya tenga un `max-height` puesto (con scroll adentro, el
// `offsetHeight` sale recortado y la cuenta de «¿entra abajo?» daría mal la segunda vez).
function contentHeight(panel) {
    return panel.scrollHeight + (panel.offsetHeight - panel.clientHeight);
}

const EscapeLayerContext = createContext(null);

/**
 * Para un despliegue DENTRO del popover (un menú o una lista que se abre adentro): mientras `handler` no sea
 * `null`, Escape lo llama primero y el popover se cierra recién con el Escape siguiente. `handler` devuelve
 * `true` si se ocupó.
 */
export function useEscapeLayer(handler) {
    const layer = useContext(EscapeLayerContext);
    useEffect(() => (layer && handler ? layer.intercept(handler) : undefined), [layer, handler]);
}

/**
 * El armazón de un popover no modal anclado a su disparador. Lo usan el de la fila de una línea con lo que
 * su regla deja afuera (`UncoveredSpendPopover`) y la tuerca del encabezado del Tablero (`ColumnSensitivityPopover`,
 * desde el Centro de Alertas v2); el de la cuenta de la tanda del 16/10 se fue con UD-5 y sus guardianes quedaron
 * en `__tests__/AnchoredPopover.test.js`. El contenido es de cada uno; acá viven la posición, el foco y el cierre.
 * `baseClassName` es la caja del panel (por defecto `.usp-pop`): quien trae la suya la cambia, y así no hereda los
 * `p` ni las medidas de `.usp-pop` (la tuerca, review C-av2-fe-centro-24).
 *
 * Va por portal a `document.body`: la tabla vive en un `.table-box` con `overflow: auto` y montado adentro se
 * recortaba. Es un diálogo NO
 * modal: toma el foco al abrirse (para que se anuncie), se cierra con Escape, con un clic afuera, si el foco
 * se va a otro lado o si la página se mueve, y avisa con `onClose({returnFocus})` si el foco tiene que
 * volver al disparador (Escape, la X) o no (un clic en otro lado).
 *
 * Todo lo que se despliegue adentro va ADENTRO de este mismo panel, sin otro portal: un menú en su propio
 * portal (`ChipMenu`) quedaría «afuera» y el clic, el foco o el scroll en una opción cerrarían el popover.
 */
export function AnchoredPopover({id, anchorRef, onClose, labelledBy, className = '', testId, baseClassName = 'usp-pop', children}) {
    const panelRef = useRef(null);
    const focused = useRef(false);
    const interceptor = useRef(null);
    const [position, setPosition] = useState(null);

    const escapeLayer = useMemo(() => ({
        intercept(handler) {
            interceptor.current = handler;
            return () => {
                if (interceptor.current === handler) interceptor.current = null;
            };
        },
    }), []);

    // Se mide antes de pintar: sin el alto no se sabe si entra abajo. La primera pasada (invisible) ya lleva
    // el ANCHO FINAL: sin él el texto se partía en menos renglones, el alto medido salía más chico que el
    // real y, con 4 o 5 campañas en una ventana de 900 px, el panel se abría abajo con el pie afuera
    // (QA-linea-02, 16/10).
    const place = useCallback(() => {
        // Con el disparador desmontado, su rect es todo 0: el panel saltaba a la esquina. Se queda donde está
        // hasta que aparezca el que lo reemplaza (review C-cs-fe-07).
        if (!anchorRef.current || !panelRef.current || !anchorRef.current.isConnected) return;
        const next = popoverPosition(anchorRef.current.getBoundingClientRect(), contentHeight(panelRef.current), viewport());
        setPosition((current) => (samePosition(current, next) ? current : next));
    }, [anchorRef]);

    // En cada render: lo que se ve cambia el alto (una campaña descartada, el despliegue de líneas).
    // `place` sólo cambia el estado si la posición es otra, así que no hay vuelta infinita.
    // Y si el foco se perdió adentro (la campana o la opción que se usó se desmontó o se deshabilitó), vuelve
    // al panel: si no, el Tab siguiente lo sacaba afuera y lo cerraba (review C-cs-fe-10).
    useLayoutEffect(() => {
        place();
        const active = document.activeElement;
        if (focused.current && panelRef.current && (!active || active === document.body)) {
            panelRef.current.focus({preventScroll: true});
        }
    });

    // Y si el panel cambia de alto después de ubicarlo (una fuente que llega tarde, un nombre que parte en
    // otro renglón), se vuelve a medir: si no, la cuenta de «¿entra abajo?» queda con el alto viejo.
    useEffect(() => {
        if (typeof ResizeObserver === 'undefined' || !panelRef.current) return undefined;
        const observer = new ResizeObserver(() => place());
        observer.observe(panelRef.current);
        return () => observer.disconnect();
    }, [place]);

    // Recién con la posición: en la primera pasada el panel está `visibility: hidden` y no toma el foco.
    useEffect(() => {
        if (!position || focused.current || !panelRef.current) return;
        focused.current = true;
        panelRef.current.focus();
    }, [position]);

    useEffect(() => {
        // El scroll de la página llega con `document` (o `window`) como destino: `window` no es un nodo.
        const inside = (node) => node instanceof Node && ((panelRef.current && panelRef.current.contains(node))
            || (anchorRef.current && anchorRef.current.contains(node)));
        // Tab desde el último control (o Shift+Tab desde el primero) saldría del panel hacia el final de
        // `<body>` o el fondo de la pantalla: se cierra y el foco vuelve al disparador, desde donde se sigue
        // (review C-cs-fe-10).
        const leavesWithTab = (event) => {
            const panel = panelRef.current;
            if (!panel || !panel.contains(document.activeElement)) return false;
            const tabbables = tabbablesOf(panel);
            const active = document.activeElement;
            if (event.shiftKey) return active === panel || active === tabbables[0];
            return !tabbables.length || active === tabbables[tabbables.length - 1];
        };
        const onKeyDown = (event) => {
            if (event.key === 'Tab') {
                if (!leavesWithTab(event)) return;
                event.preventDefault();
                onClose({returnFocus: true});
                return;
            }
            if (event.key !== 'Escape') return;
            if (interceptor.current && interceptor.current()) return;
            onClose({returnFocus: true});
        };
        const onMouseDown = (event) => {
            if (!inside(event.target)) onClose({returnFocus: false});
        };
        const onFocusIn = (event) => {
            if (!inside(event.target)) onClose({returnFocus: false});
        };
        // El panel es `fixed`: si la página (o la tabla) se mueve, queda flotando lejos de su disparador.
        // El scroll de las listas de adentro no cuenta.
        const onScroll = (event) => {
            if (!inside(event.target)) onClose({returnFocus: false});
        };
        // El teclado del celular achica la ventana al escribir en el buscador del panel: ahí se reubica en vez
        // de cerrarse (review C-cs-fe-16).
        const onResize = () => {
            const active = document.activeElement;
            if (panelRef.current && panelRef.current.contains(active) && active.tagName === 'INPUT') {
                place();
                return;
            }
            onClose({returnFocus: false});
        };
        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('focusin', onFocusIn);
        window.addEventListener('scroll', onScroll, {passive: true, capture: true});
        window.addEventListener('resize', onResize, {passive: true});
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('focusin', onFocusIn);
            window.removeEventListener('scroll', onScroll, {capture: true});
            window.removeEventListener('resize', onResize);
        };
    }, [anchorRef, onClose, place]);

    // El scroll de adentro va acá y no en la caja: con `baseClassName` propio (la tuerca del Tablero clásico, sin
    // `.ui-v2`) el recorte no tendría scroll.
    const style = position
        ? {top: position.top, left: position.left, width: position.width, maxHeight: position.maxHeight || undefined,
            overflowY: position.maxHeight ? 'auto' : undefined}
        // Primera pasada: se dibuja invisible, con el ancho final, para medir el alto.
        : {top: 0, left: 0, width: popoverWidth(viewport()), visibility: 'hidden'};

    return createPortal(
        // El click no sube: el panel del cliente expande la fila al tocarla, y React propaga los eventos
        // de un portal por el árbol de componentes, no por el del DOM.
        <div className={`${baseClassName}${className ? ` ${className}` : ''}`} role="dialog" aria-modal="false"
             aria-labelledby={labelledBy} id={id} ref={panelRef} tabIndex={-1} style={style}
             onClick={(event) => event.stopPropagation()} data-testid={testId}>
            <EscapeLayerContext.Provider value={escapeLayer}>
                {children}
            </EscapeLayerContext.Provider>
        </div>,
        document.body);
}

export default AnchoredPopover;
